import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Youtube, Send, Crown } from "lucide-react";
import { NAV, BRAND } from "../data/site";
import { subscribeNewsletter } from "../lib/api";
import { useOrgSettings } from "../lib/org";

export const NewsletterInline = ({ dark = false, idPrefix = "" }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're in the Village! Check your inbox 💌");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex w-full max-w-md flex-col gap-3 sm:flex-row" data-testid={`${idPrefix}newsletter-form`}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        data-testid={`${idPrefix}newsletter-email-input`}
        className="w-full border border-silver/30 bg-charcoal px-4 py-3 font-body font-medium text-cream outline-none transition-colors placeholder:text-silver/40 focus:border-gold"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid={`${idPrefix}newsletter-submit-btn`}
        className="flex shrink-0 items-center justify-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-5 py-3 font-head text-sm font-extrabold uppercase tracking-[0.15em] text-onyx transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
      >
        <Send size={16} /> {loading ? "..." : "Join"}
      </button>
    </form>
  );
};

export const Footer = () => {
  const org = useOrgSettings();
  return (
  <footer className="grain-dark relative border-t border-gold/30 bg-onyx text-cream" data-testid="footer">
    <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
    <div className="relative mx-auto max-w-[1400px] px-4 py-16 sm:px-6">
      <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <div className="flex items-center gap-2.5">
            <Crown className="h-7 w-7 text-gold" strokeWidth={1.5} />
            <span className="font-display text-2xl uppercase leading-none tracking-wide text-metallic">
              Big Little Village
            </span>
          </div>
          <p className="mt-4 max-w-xs font-body text-base leading-relaxed text-silver/85">{BRAND.tagline}</p>
          <div className="mt-5 space-y-1 font-mono text-[10px] uppercase tracking-[0.18em] text-silver/55" data-testid="footer-org-credentials">
            <p className="text-silver/70">{org.org_tagline}</p>
            <p>{org.nonprofit_designation}</p>
            {org.ein && <p>EIN: {org.ein}</p>}
            <p>Founding Cohorts: {org.founding_location}</p>
          </div>
          <div className="mt-4 space-y-1 font-mono text-[10px] uppercase tracking-[0.15em]" data-testid="footer-org-emails">
            <p className="text-silver/55">General: <a href={`mailto:${org.email_general}`} data-testid="footer-email-general" className="text-goldlight/80 transition-colors hover:text-goldlight">{org.email_general}</a></p>
            <p className="text-silver/55">Partnerships: <a href={`mailto:${org.email_partnerships}`} data-testid="footer-email-partnerships" className="text-goldlight/80 transition-colors hover:text-goldlight">{org.email_partnerships}</a></p>
          </div>
          <div className="mt-6">
            <p className="mb-2 font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Get the newsletter</p>
            <NewsletterInline dark idPrefix="footer-" />
          </div>
        </div>

        <div>
          <h4 className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Explore</h4>
          <ul className="mt-4 space-y-2">
            <li><Link to="/" className="font-body font-medium text-silver transition-colors hover:text-goldlight">Home</Link></li>
            {NAV.map((n) => (
              <li key={n.to}><Link to={n.to} className="font-body font-medium text-silver transition-colors hover:text-goldlight">{n.label}</Link></li>
            ))}
            <li><Link to="/apply" className="font-body font-medium text-silver transition-colors hover:text-goldlight">Apply</Link></li>
            <li><Link to="/contact" data-testid="footer-contact-link" className="font-body font-medium text-silver transition-colors hover:text-goldlight">Contact</Link></li>
            <li><Link to="/press" data-testid="footer-press-link" className="font-body font-medium text-silver transition-colors hover:text-goldlight">Press Kit</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Follow the movement</h4>
          <div className="mt-4 flex gap-3">
            {[Instagram, Youtube, Send].map((Icon, i) => (
              <a key={i} href="#" aria-label="social" className="border border-silver/30 p-2 text-silver transition-colors hover:border-gold hover:text-goldlight">
                <Icon size={18} />
              </a>
            ))}
          </div>
          <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-silver/50">A nonprofit creative career pipeline</p>
        </div>
      </div>

      <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-gold/20 pt-6 sm:flex-row sm:items-center">
        <p className="font-mono text-xs text-silver/50">© {new Date().getFullYear()} Big Little Village. All rights reserved.</p>
        <div className="flex gap-5">
          <Link to="/transmission/education" data-testid="footer-education-clearance" className="font-mono text-[9px] uppercase tracking-[0.3em] text-silver/40 transition-colors hover:text-goldlight">
            Education Clearance
          </Link>
          <Link to="/transmission/industry" data-testid="footer-industry-clearance" className="font-mono text-[9px] uppercase tracking-[0.3em] text-silver/40 transition-colors hover:text-goldlight">
            Industry Clearance
          </Link>
        </div>
        <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-goldlight">We are little. But our ideas are big.</p>
      </div>
    </div>
  </footer>
  );
};
